import React, { useState, useEffect } from "react";
import {
  Phone,
  MessageCircle,
  Instagram,
  MapPin,
  Clock,
  Menu as MenuIcon,
  X,
  Sparkles,
  ChevronRight,
} from "lucide-react";

// Brand constants — tel/whatsapp links
const PHONE_DISPLAY = "+39 351 3842445";
const PHONE_TEL = "tel:+393513842445";
const WHATSAPP_URL = "https://wa.me/393513842445";
const INSTAGRAM_URL = "https://instagram.com/orietta1975";
const ADDRESS = "Viale Guglielmo Marconi 57, Rosolina (RO)";
const MAPS_URL = "https://maps.google.com/?q=Viale+Guglielmo+Marconi+57+Rosolina";

// High-quality Unsplash images
const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_orietta-gourmet/artifacts/ab6ul8ft_ba136cf9-4c31-4afa-9852-095f7cb45565.jpeg",
  pasta: "https://customer-assets.emergentagent.com/job_orietta-gourmet/artifacts/595q6fel_53b635ba-6fe9-4223-8fc2-82e3492dfda3.jpeg",
  brace: "https://customer-assets.emergentagent.com/job_orietta-gourmet/artifacts/ab6ul8ft_ba136cf9-4c31-4afa-9852-095f7cb45565.jpeg",
  dolci: "https://customer-assets.emergentagent.com/job_orietta-gourmet/artifacts/ojj8ngbe_2ac9702e-7143-4891-af70-8788e70f123e.jpeg",
  chef: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1400&q=80",
};

// All real dish photos (Orietta's authentic dishes)
const ASSETS_BASE = "https://customer-assets.emergentagent.com/job_orietta-gourmet/artifacts";
const DISHES = {
  panino: `${ASSETS_BASE}/gj2nm3ot_55e35551-43d4-43da-a405-8d6135576e53.jpeg`,
  gnocchi_ragu: `${ASSETS_BASE}/595q6fel_53b635ba-6fe9-4223-8fc2-82e3492dfda3.jpeg`,
  polpette: `${ASSETS_BASE}/09u80ywu_d3be5776-5e45-4a9f-87ea-18542b0cd7a0.jpeg`,
  caprese: `${ASSETS_BASE}/8z7lf66e_3efd1f88-7cae-4f8f-bc01-aa468171f86b.jpeg`,
  baccala: `${ASSETS_BASE}/bfe6ao4f_aa95157d-ce49-479a-9d2d-f274b1f52e84.jpeg`,
  spezzatino: `${ASSETS_BASE}/gy7zxda6_e23d2587-646b-4076-9b93-d0d0a101a74c.jpeg`,
  torta_mele: `${ASSETS_BASE}/ojj8ngbe_2ac9702e-7143-4891-af70-8788e70f123e.jpeg`,
  brace: `${ASSETS_BASE}/ab6ul8ft_ba136cf9-4c31-4afa-9852-095f7cb45565.jpeg`,
  pappardelle: `${ASSETS_BASE}/lzmh87f6_bc7dedd2-cf47-4176-926d-69245cad55d9.jpeg`,
  pollo_arrosto: `${ASSETS_BASE}/2ugx7brr_1402c5b5-f10f-4cde-a87c-6218e7453bfc.jpeg`,
  tagliatelle_pesce: `${ASSETS_BASE}/kbigb5oi_70426d65-7b38-4bb0-bb08-73690ab9f7f2.jpeg`,
};

const NAV_LINKS = [
  { href: "#storia", label: "La Storia" },
  { href: "#specialita", label: "Specialità" },
  { href: "#galleria", label: "Galleria" },
  { href: "#menu", label: "Menu" },
  { href: "#promozione", label: "Promo" },
  { href: "#contatti", label: "Contatti" },
];

const AnnouncementBar = () => (
  <div
    data-testid="announcement-bar"
    className="w-full bg-[#4A7A3A] text-white text-center text-sm md:text-[15px] font-semibold tracking-wide py-3 px-4"
    style={{ fontFamily: "'Montserrat', sans-serif" }}
  >
    <span className="inline-flex items-center gap-2 flex-wrap justify-center">
      <Sparkles className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span>
        <strong className="font-bold">NUOVA APERTURA:</strong> Ti aspettiamo
        Sabato 23 Maggio dalle ore 17:00 in Viale Guglielmo Marconi 57, Rosolina!
      </span>
    </span>
  </div>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md shadow-sm"
          : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-4 flex items-center justify-between">
        <a
          href="#top"
          data-testid="navbar-logo"
          className="flex items-baseline gap-2 group"
        >
          <span
            className="text-[22px] md:text-2xl text-[#4A7A3A] leading-none font-bold italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            La Cucina
          </span>
          <span
            className="text-[18px] md:text-xl text-gray-800 leading-none"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            di Orietta
          </span>
        </a>

        <ul
          className="hidden lg:flex items-center gap-10 text-[15px] text-gray-800"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          {NAV_LINKS.map((l) => (
            <li key={l.href}>
              <a
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                href={l.href}
                className="relative font-medium hover:text-[#4A7A3A] transition-colors duration-200 after:content-[''] after:absolute after:-bottom-1 after:left-0 after:w-0 after:h-[2px] after:bg-[#4A7A3A] after:transition-all after:duration-300 hover:after:w-full"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3">
          <a
            data-testid="navbar-call-button"
            href={PHONE_TEL}
            className="hidden sm:inline-flex items-center gap-2 bg-[#4A7A3A] hover:bg-[#3d6630] text-white px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <Phone className="w-4 h-4" aria-hidden="true" />
            Chiama Ora
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen((s) => !s)}
            aria-label="Apri menu"
            className="lg:hidden p-2 text-gray-800 hover:text-[#4A7A3A] transition-colors"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-stone-200 bg-white/95 backdrop-blur-md"
        >
          <ul className="px-5 py-4 flex flex-col gap-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {NAV_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="block py-3 px-2 text-gray-800 font-medium hover:text-[#4A7A3A] border-b border-stone-100 last:border-0"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-3">
              <a
                data-testid="mobile-call-button"
                href={PHONE_TEL}
                className="flex items-center justify-center gap-2 bg-[#4A7A3A] text-white px-5 py-3 rounded-xl font-semibold"
              >
                <Phone className="w-4 h-4" /> Chiama Ora
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative w-full min-h-[88vh] md:min-h-[92vh] flex items-center overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={IMG.hero}
        alt="Pasta fresca artigianale su tagliere di legno"
        className="w-full h-full object-cover scale-105"
        loading="eager"
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, rgba(20,30,15,0.78) 0%, rgba(20,30,15,0.55) 55%, rgba(20,30,15,0.35) 100%)",
        }}
      />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-12 py-24 md:py-32 w-full">
      <div className="max-w-3xl">
        <div
          className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md border border-white/25 rounded-full px-4 py-1.5 mb-6"
          data-testid="hero-badge"
        >
          <span className="w-2 h-2 rounded-full bg-[#9CCB6F] animate-pulse" />
          <span
            className="text-white/95 text-xs md:text-sm font-medium tracking-wider uppercase"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Gastronomia Artigianale · Rosolina
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] mb-6"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          Il Piacere della Tradizione,
          <br />
          <span className="italic text-[#D9E8C5]">Direttamente a Casa Tua.</span>
        </h1>

        <p
          data-testid="hero-subtitle"
          className="text-white/90 text-base md:text-lg lg:text-xl leading-relaxed max-w-2xl mb-10"
          style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
        >
          Scopri la nuova gastronomia artigianale di Rosolina. Ingredienti
          genuini, ricette di famiglia e tanto amore in ogni piatto.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <a
            data-testid="hero-cta-primary"
            href="#specialita"
            className="inline-flex items-center justify-center gap-2 bg-[#4A7A3A] hover:bg-[#3d6630] text-white px-7 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Scopri il Menu
            <ChevronRight className="w-4 h-4" />
          </a>
          <a
            data-testid="hero-cta-secondary"
            href="#promozione"
            className="inline-flex items-center justify-center gap-2 border-2 border-white/80 hover:border-white text-white hover:bg-white hover:text-[#4A7A3A] px-7 py-4 rounded-xl text-base font-semibold transition-all duration-300"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Vieni all'Inaugurazione
          </a>
        </div>
      </div>
    </div>

    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/70">
      <span
        className="text-[11px] uppercase tracking-[0.3em]"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        Scorri
      </span>
      <div className="w-px h-10 bg-white/40" />
    </div>
  </section>
);

const PromoSection = () => (
  <section
    id="promozione"
    data-testid="promo-section"
    className="bg-[#FDFBF7] py-20 md:py-28 px-5 md:px-8"
  >
    <div className="max-w-5xl mx-auto">
      <div
        data-testid="promo-card"
        className="relative bg-white border-[3px] border-dashed border-[#4A7A3A] rounded-2xl p-8 md:p-14 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
      >
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-[#4A7A3A] text-white px-5 py-2 rounded-full text-xs uppercase tracking-[0.25em] font-bold shadow-md"
          style={{ fontFamily: "'Montserrat', sans-serif" }}>
          Offerta Lancio
        </div>

        <div className="grid md:grid-cols-[auto_1fr_auto] gap-8 items-center">
          <div className="text-5xl md:text-6xl select-none">🎁</div>

          <div className="text-center md:text-left">
            <h2
              className="text-3xl md:text-4xl lg:text-5xl text-gray-800 leading-tight mb-3"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
            >
              Festeggia con noi!
            </h2>
            <p
              className="text-gray-800/85 text-base md:text-lg leading-relaxed"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Torna a trovarci dopo l'inaugurazione e ricevi un{" "}
              <strong className="text-[#4A7A3A] font-bold">
                BUONO SCONTO DEL 10%
              </strong>{" "}
              su tutte le nostre specialità da asporto.
            </p>
          </div>

          <div className="text-center bg-[#4A7A3A] text-white rounded-xl px-6 py-5 shadow-md hidden md:block">
            <div
              className="text-5xl font-black leading-none"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              10
              <span className="text-2xl align-top">%</span>
            </div>
            <div
              className="text-[10px] uppercase tracking-[0.2em] mt-1 opacity-90"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              di sconto
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const SpecialtyCard = ({ image, alt, title, description, testId }) => (
  <article
    data-testid={testId}
    className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col"
  >
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={image}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
    <div className="p-7 md:p-8 flex-1 flex flex-col">
      <h3
        className="text-2xl md:text-[26px] text-gray-800 mb-3"
        style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
      >
        {title}
      </h3>
      <p
        className="text-gray-800/80 text-[15px] leading-relaxed flex-1"
        style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}
      >
        {description}
      </p>
      <div className="mt-6 pt-5 border-t border-stone-100 flex items-center justify-between">
        <span
          className="text-xs uppercase tracking-[0.2em] text-[#4A7A3A] font-bold"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Fatto in casa
        </span>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testId}-order`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-800 hover:text-[#4A7A3A] transition-colors"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Ordina <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </article>
);

const SpecialtiesSection = () => (
  <section
    id="specialita"
    data-testid="specialties-section"
    className="bg-[#FDFBF7] py-20 md:py-28 px-5 md:px-8"
  >
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-14 md:mb-20">
        <span
          className="inline-block text-xs uppercase tracking-[0.3em] text-[#4A7A3A] font-bold mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          — Il Nostro Menu
        </span>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl text-gray-800 leading-[1.05] mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          Le Nostre <em className="text-[#4A7A3A] not-italic">Specialità</em>
        </h2>
        <p
          className="text-gray-800/75 text-base md:text-lg leading-relaxed"
          style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
        >
          Ogni piatto nasce da mani esperte, materie prime selezionate e il
          tempo necessario per fare le cose come si deve.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7 md:gap-8">
        <SpecialtyCard
          testId="specialty-card-primi"
          image={IMG.pasta}
          alt="Tagliatelle al ragù fatte a mano"
          title="I Primi del Cuore"
          description="Tagliatelle artigianali trafilate al bronzo, pasta fresca e sughi ricchi preparati a lenta cottura, proprio come la ricetta della nonna."
        />
        <SpecialtyCard
          testId="specialty-card-brace"
          image={IMG.brace}
          alt="Carne alla brace fumante"
          title="La Brace e gli Arrosti"
          description="Selezioni di carne di prima qualità, costine succulente e arrosti dal profumo inconfondibile, pronti da portare in tavola."
        />
        <SpecialtyCard
          testId="specialty-card-dolci"
          image={IMG.dolci}
          alt="Torta di mele rustica casalinga"
          title="La Pasticceria Casalinga"
          description="Torte di mele soffici, crostate fragranti e dolci al cucchiaio. Il finale perfetto per ogni tuo pasto."
        />
      </div>
    </div>
  </section>
);

const StorySection = () => (
  <section
    id="storia"
    data-testid="story-section"
    className="bg-white py-20 md:py-28 px-5 md:px-8"
  >
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <div className="order-2 lg:order-1">
        <span
          className="inline-block text-xs uppercase tracking-[0.3em] text-[#4A7A3A] font-bold mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          — Chi Siamo
        </span>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl text-gray-800 leading-[1.05] mb-7"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          La Storia di <em className="text-[#4A7A3A] not-italic">Orietta</em>
        </h2>
        <div
          className="space-y-5 text-gray-800/85 text-base md:text-lg leading-[1.85]"
          style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}
        >
          <p>
            Tutto è iniziato in una cucina di famiglia, tra l'odore del soffritto
            e le mani infarinate sul tagliere. Per Orietta, cucinare non è mai
            stato un mestiere: è il modo più sincero di prendersi cura delle
            persone.
          </p>
          <p>
            Dopo anni passati a perfezionare ricette tramandate di generazione
            in generazione, ha deciso di aprire le porte della sua cucina a
            tutta <strong className="text-gray-800">Rosolina</strong>. Ogni
            ingrediente è scelto con cura maniacale: pasta fresca tirata a mano,
            verdure dei produttori locali, carni selezionate dai macellai di
            fiducia.
          </p>
          <p className="italic text-[#4A7A3A]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}>
            "Voglio che ogni piatto da portar via abbia lo stesso sapore di un
            pranzo della domenica a casa."
          </p>
        </div>

        <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
          {[
            { n: "100%", l: "Ingredienti locali" },
            { n: "30+", l: "Ricette di famiglia" },
            { n: "Ogni dì", l: "Pasta fresca" },
          ].map((s, i) => (
            <div key={i} data-testid={`story-stat-${i}`}>
              <div
                className="text-2xl md:text-3xl text-[#4A7A3A] mb-1"
                style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
              >
                {s.n}
              </div>
              <div
                className="text-[11px] md:text-xs uppercase tracking-[0.15em] text-gray-800/70"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="order-1 lg:order-2 relative">
        <div className="absolute -inset-3 md:-inset-5 bg-[#4A7A3A]/10 rounded-3xl rotate-2" />
        <div className="absolute -inset-1 bg-[#FDFBF7] rounded-3xl -rotate-1" />
        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-2xl">
          <img
            src={IMG.chef}
            alt="Le mani di Orietta che impastano la pasta fresca"
            loading="lazy"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    id="contatti"
    data-testid="footer"
    className="bg-[#2C3A24] text-white pt-20 pb-10 px-5 md:px-8"
  >
    <div className="max-w-7xl mx-auto">
      <div className="grid md:grid-cols-3 gap-12 mb-14">
        {/* Indirizzo */}
        <div data-testid="footer-address">
          <div className="flex items-center gap-3 mb-5 text-[#9CCB6F]">
            <MapPin className="w-5 h-5" />
            <span
              className="text-xs uppercase tracking-[0.25em] font-bold"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Vieni a trovarci
            </span>
          </div>
          <h3
            className="text-2xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
          >
            Indirizzo
          </h3>
          <p
            className="text-white/85 text-[15px] leading-relaxed mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Viale Guglielmo Marconi 57
            <br />
            45010 Rosolina (RO)
          </p>
          <a
            data-testid="footer-maps-link"
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#9CCB6F] hover:text-white transition-colors"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Apri in Maps <ChevronRight className="w-4 h-4" />
          </a>
        </div>

        {/* Orari */}
        <div data-testid="footer-hours">
          <div className="flex items-center gap-3 mb-5 text-[#9CCB6F]">
            <Clock className="w-5 h-5" />
            <span
              className="text-xs uppercase tracking-[0.25em] font-bold"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Quando siamo aperti
            </span>
          </div>
          <h3
            className="text-2xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
          >
            Orari
          </h3>
          <ul
            className="space-y-2 text-[15px] text-white/85"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <li className="flex justify-between border-b border-white/10 pb-2">
              <span>Martedì – Domenica</span>
              <span className="text-white font-medium">09:30 – 13:30</span>
            </li>
            <li className="flex justify-between border-b border-white/10 pb-2">
              <span>&nbsp;</span>
              <span className="text-white font-medium">17:00 – 20:00</span>
            </li>
            <li className="flex justify-between text-white/60">
              <span>Lunedì</span>
              <span className="italic">Chiuso</span>
            </li>
          </ul>
        </div>

        {/* Social & Contatti */}
        <div data-testid="footer-contacts">
          <div className="flex items-center gap-3 mb-5 text-[#9CCB6F]">
            <Phone className="w-5 h-5" />
            <span
              className="text-xs uppercase tracking-[0.25em] font-bold"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Restiamo in contatto
            </span>
          </div>
          <h3
            className="text-2xl mb-3"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
          >
            Social & Contatti
          </h3>
          <div
            className="space-y-3"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <a
              data-testid="footer-phone-link"
              href={PHONE_TEL}
              className="flex items-center gap-3 text-[15px] text-white/90 hover:text-[#9CCB6F] transition-colors group"
            >
              <span className="w-9 h-9 rounded-full bg-white/10 group-hover:bg-[#4A7A3A] flex items-center justify-center transition-colors">
                <Phone className="w-4 h-4" />
              </span>
              {PHONE_DISPLAY}
            </a>
            <a
              data-testid="footer-whatsapp-link"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[15px] text-white/90 hover:text-[#9CCB6F] transition-colors group"
            >
              <span className="w-9 h-9 rounded-full bg-white/10 group-hover:bg-[#4A7A3A] flex items-center justify-center transition-colors">
                <MessageCircle className="w-4 h-4" />
              </span>
              Ordina su WhatsApp
            </a>
            <a
              data-testid="footer-instagram-link"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 text-[15px] text-white/90 hover:text-[#9CCB6F] transition-colors group"
            >
              <span className="w-9 h-9 rounded-full bg-white/10 group-hover:bg-[#4A7A3A] flex items-center justify-center transition-colors">
                <Instagram className="w-4 h-4" />
              </span>
              @orietta1975
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row gap-4 items-center justify-between">
        <p
          className="text-sm text-white/55"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          © {new Date().getFullYear()} La Cucina di Orietta — Tutti i diritti
          riservati.
        </p>
        <p
          className="text-xs italic text-white/45"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          Fatto con cura, come ogni piatto.
        </p>
      </div>
    </div>

    {/* Floating WhatsApp button (mobile) */}
    <a
      data-testid="floating-whatsapp"
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Ordina su WhatsApp"
      className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform duration-200"
    >
      <MessageCircle className="w-7 h-7" />
    </a>
  </footer>
);

// ───────────────────────── GALLERY SECTION ─────────────────────────
const GALLERY_ITEMS = [
  { src: DISHES.brace, alt: "Grigliata mista alla brace", label: "Brace" },
  { src: DISHES.tagliatelle_pesce, alt: "Tagliatelle al ragù di pesce", label: "Tagliatelle di Pesce" },
  { src: DISHES.pollo_arrosto, alt: "Pollo arrosto dorato", label: "Pollo Arrosto" },
  { src: DISHES.polpette, alt: "Polpette al sugo di pomodoro", label: "Polpette" },
  { src: DISHES.pappardelle, alt: "Pappardelle ai funghi", label: "Pappardelle ai Funghi" },
  { src: DISHES.spezzatino, alt: "Spezzatino con polenta", label: "Spezzatino & Polenta" },
  { src: DISHES.baccala, alt: "Baccalà alla vicentina", label: "Baccalà & Polenta" },
  { src: DISHES.caprese, alt: "Torta caprese al cioccolato", label: "Caprese" },
  { src: DISHES.panino, alt: "Panino gourmet tostato", label: "Panini Gourmet" },
];

const GallerySection = () => (
  <section
    id="galleria"
    data-testid="gallery-section"
    className="bg-white py-20 md:py-28 px-5 md:px-8"
  >
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-12 md:mb-16">
        <span
          className="inline-block text-xs uppercase tracking-[0.3em] text-[#4A7A3A] font-bold mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          — Direttamente dalla cucina
        </span>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl text-gray-800 leading-[1.05] mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          Una <em className="text-[#4A7A3A] not-italic">Galleria</em> di Sapori
        </h2>
        <p
          className="text-gray-800/75 text-base md:text-lg leading-relaxed"
          style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
        >
          Ogni foto è uno scatto reale dalla nostra cucina. Niente filtri, solo
          ingredienti veri e amore per la tradizione.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-5">
        {GALLERY_ITEMS.map((item, idx) => {
          const tall = idx === 0 || idx === 4 || idx === 7;
          return (
            <figure
              key={idx}
              data-testid={`gallery-item-${idx}`}
              className={`group relative overflow-hidden rounded-2xl shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer ${
                tall ? "row-span-2 aspect-[3/4] md:aspect-[4/5]" : "aspect-square"
              }`}
            >
              <img
                src={item.src}
                alt={item.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption
                className="absolute bottom-0 left-0 right-0 p-4 md:p-5 text-white translate-y-2 group-hover:translate-y-0 transition-transform duration-500"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <span className="text-xs uppercase tracking-[0.2em] text-[#9CCB6F] font-bold block mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  Specialità
                </span>
                <span className="text-lg md:text-xl font-semibold leading-tight">
                  {item.label}
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  </section>
);

// ───────────────────────── MENU SECTION ─────────────────────────
const MENU_PRIMI = [
  { name: "Lasagne alla Bolognese" },
  { name: "Lasagne Radicchio e Salsiccia" },
  { name: "Lasagne alla Marinara" },
  { name: "Parmigiana di Melanzane" },
  { name: "Gnocchi al Pomodoro" },
  { name: "Gnocchi al Ragù" },
  { name: "Gnocchi alla Marinara" },
  { name: "Crespelle di Pesce" },
  { name: "Crespelle Ricotta e Spinaci" },
  { name: "Arancini Classici / Vegetariani" },
  { name: "Riso Freddo · Pasta Fredda · Orzo · Farro" },
];

const MENU_SECONDI = [
  { name: "Pollo allo Spiedo" },
  { name: "Pollo alla Griglia" },
  { name: "Scaloppine al Limone / Pizzaiola" },
  { name: "Hamburger di Manzo" },
  { name: "Hamburger di Pollo" },
  { name: "Polpette al Sugo" },
  { name: "Verdure Ripiene" },
  { name: "Arrosto di Lonza" },
  { name: "Gulash" },
  { name: "Vitello Tonnato" },
  { name: "Straccetti di Pollo" },
  { name: "Straccetti di Manzo alla Mediterranea" },
  { name: "Polpette di Patate ai Funghi" },
  { name: "Involtini di Carne" },
  { name: "Rotolo di Tacchino Ripieno" },
];

const MenuColumn = ({ title, items, testId }) => (
  <div data-testid={testId} className="bg-white rounded-2xl shadow-lg p-7 md:p-10 border border-stone-100">
    <h3
      className="text-2xl md:text-3xl text-[#4A7A3A] mb-1"
      style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
    >
      {title}
    </h3>
    <div className="w-12 h-[2px] bg-[#4A7A3A] mb-7" />
    <ul className="space-y-3.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-center gap-3 text-[15px] md:text-base text-gray-800 font-medium leading-snug py-1 border-b border-stone-100 last:border-0"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A7A3A]/60 flex-shrink-0" />
          {it.name}
        </li>
      ))}
    </ul>
  </div>
);

const MenuSection = () => (
  <section
    id="menu"
    data-testid="menu-section"
    className="bg-[#FDFBF7] py-20 md:py-28 px-5 md:px-8 relative overflow-hidden"
  >
    {/* Decorative background pattern */}
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage:
          "radial-gradient(circle at 1px 1px, #4A7A3A 1px, transparent 0)",
        backgroundSize: "32px 32px",
      }}
    />

    <div className="max-w-7xl mx-auto relative">
      <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
        <span
          className="inline-block text-xs uppercase tracking-[0.3em] text-[#4A7A3A] font-bold mb-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          — Il Menu Completo
        </span>
        <h2
          className="text-4xl sm:text-5xl lg:text-6xl text-gray-800 leading-[1.05] mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
        >
          Tutto quello che <em className="text-[#4A7A3A] not-italic">cuciniamo per te</em>
        </h2>
        <p
          className="text-gray-800/75 text-base md:text-lg leading-relaxed"
          style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
        >
          Piatti pronti da asporto, preparati ogni giorno con ingredienti
          freschi e ricette di famiglia.
        </p>
      </div>

      {/* SPECIALITÀ Highlight Card */}
      <div
        data-testid="menu-specialty-highlight"
        className="relative mb-14 md:mb-20 rounded-2xl overflow-hidden shadow-2xl group"
      >
        <div className="absolute inset-0">
          <img
            src={DISHES.brace}
            alt="Costine alla brace"
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#2C3A24]/95 via-[#2C3A24]/75 to-[#2C3A24]/40" />
        </div>
        <div className="relative p-8 md:p-14 lg:p-16 text-white max-w-2xl">
          <div className="inline-flex items-center gap-2 bg-[#9CCB6F]/20 backdrop-blur-sm border border-[#9CCB6F]/40 rounded-full px-4 py-1.5 mb-6">
            <Sparkles className="w-4 h-4 text-[#9CCB6F]" />
            <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#D9E8C5]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              La Specialità della Casa
            </span>
          </div>
          <h3
            className="text-3xl md:text-4xl lg:text-5xl mb-4 leading-[1.1]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
          >
            Costine cotte a <em className="text-[#9CCB6F] not-italic">bassa temperatura</em> in salsa BBQ
          </h3>
          <p
            className="text-white/90 text-base md:text-lg leading-relaxed mb-6 max-w-xl"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            Cottura lenta che esalta ogni fibra della carne, glassate con la
            nostra salsa BBQ fatta in casa. Si sciolgono in bocca.
          </p>
          <a
            data-testid="menu-specialty-order"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#9CCB6F] hover:bg-white hover:text-[#4A7A3A] text-[#2C3A24] px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5 shadow-lg"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <MessageCircle className="w-4 h-4" />
            Ordinala ora
          </a>
        </div>
      </div>

      {/* Menu Grid */}
      <div className="grid lg:grid-cols-2 gap-7 md:gap-10">
        <MenuColumn title="Primi Piatti" items={MENU_PRIMI} testId="menu-column-primi" />
        <MenuColumn title="Secondi di Carne" items={MENU_SECONDI} testId="menu-column-secondi" />
      </div>

      <p
        className="text-center text-sm text-gray-800/60 mt-10 italic max-w-2xl mx-auto"
        style={{ fontFamily: "'Montserrat', sans-serif" }}
      >
        Tutti i piatti sono preparati al momento con ingredienti freschi.
      </p>

      <div className="text-center mt-10">
        <a
          data-testid="menu-cta-order"
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#4A7A3A] hover:bg-[#3d6630] text-white px-7 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          <MessageCircle className="w-5 h-5" />
          Ordina su WhatsApp
          <ChevronRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  </section>
);

export default function LaCucinaDiOrietta() {
  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-[#FDFBF7] text-gray-800"
      style={{ fontFamily: "'Montserrat', sans-serif" }}
    >
      <AnnouncementBar />
      <Navbar />
      <main>
        <Hero />
        <PromoSection />
        <SpecialtiesSection />
        <GallerySection />
        <MenuSection />
        <StorySection />
      </main>
      <Footer />
    </div>
  );
}
